import React from "react";
import * as t from "io-ts";
import { TBlockContainerStateProps } from "../../state/TBlockContainerStateProps";
import { getJson, postJson } from "../../wrappers/restRequest";
import * as FirstPartyFetchResponse from "../../../../domain/models/FirstPartyFetchResponse";
import { CRMBlockScrollCacheView } from "../../components/CRM/CRMBlock/CRMBlockViews/CRMBlockViews";
import { BackgroundColour } from "../../components/BuildingBlocks/BackgroundColour";
import { MinHeight } from "../../components/BuildingBlocks/MinHeight";
import { Padding } from "../../components/BuildingBlocks/Padding";
import { SpacingColumn } from "../../components/BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../components/BuildingBlocks/SpacingRow";
import { CRMSingleViewHeader } from "../../components/Simple/CRMSingleViewHeader/CRMSingleViewHeader";
import { CRMTitleSection } from "../../components/CRMTitleSection/CRMTitleSection";
import { CRMParagraph } from "../../components/Simple/CRMParagraph/CRMParagraph";
import { WeightBold } from "../../components/WeightBold/WeightBold";
import { CRMButtonPrimary } from "../../components/CRMButtonPrimary/CRMButtonPrimary";
import CRMTextAreaComponent from "../../components/CRMTextAreaComponent/CRMTextAreaComponent";
import { CRMInputGeneralComponent } from "../../components/CRMInputs/CRMInputGeneralComponent/CRMInputGeneralComponent";
import { CRMRadioListComponent } from "../../components/CRMInputs/CRMRadioListComponent/CRMRadioListComponent";
import { CRMInputLabel } from "../../components/CRMInputLabel/CRMInputLabel";
import { CRMColors } from "../../models/CRMColors";
import { CRMSpacing } from "../../models/CRMSpacing";

const HmlrPartyDetailsCodec = t.union([
    t.intersection([t.type({ type: t.literal("PRIVATE_INDIVIDUAL"), surname: t.string }), t.partial({ forenames: t.string })]),
    t.intersection([t.type({ type: t.literal("UK_COMPANY"), organisation_name: t.string }), t.partial({ company_number: t.string })]),
    t.intersection([t.type({ type: t.literal("OVERSEAS_COMPANY"), organisation_name: t.string, incorporation_territory: t.string, roe_id_exempt: t.boolean }), t.partial({ company_number: t.string, roe_id: t.string })]),
    t.type({ type: t.literal("OTHER_ORGANISATION"), organisation_name: t.string }),
    t.type({ type: t.literal("UNKNOWN"), freeformat_name: t.string }),
]);

const HmlrPartyCodec = t.intersection([
    t.type({
        details: HmlrPartyDetailsCodec,
        address_for_service_option: t.string,
        representation_type: t.string,
    }),
    t.partial({
        addresses: t.array(t.string),
        identity_evidence_type: t.string,
        register_action: t.string,
    }),
]);

const HmlrDischargeSubmissionTypeCodec = t.union([
    t.literal("ATTACHED"),
    t.literal("LENDER"),
    t.literal("LATER"),
]);

type TDischargeSubmissionType = t.TypeOf<typeof HmlrDischargeSubmissionTypeCodec>;

const HmlrTransactionCodec = t.intersection([
    t.type({ type: t.string, fee_in_pence: t.number, priority: t.number }),
    t.partial({
        details: t.partial({
            value: t.number,
            type: t.string,
            mdref: t.string,
            discharge_submission_type: HmlrDischargeSubmissionTypeCodec,
        }),
        documents: t.array(t.string),
        relates_to_new_title: t.boolean,
        title_references: t.array(t.string),
    }),
]);

const HmlrTitleDetailsCodec = t.intersection([
    t.type({ title_number: t.string, title_type: t.string }),
    t.type({ scope_details: t.intersection([t.type({ scope: t.string }), t.partial({ part_description: t.string })]) }),
    t.partial({ disclosable_overriding_interests: t.boolean }),
]);

const HmlrDocumentMetadataCodec = t.type({
    certification_statement_type: t.string,
    document_filename: t.string,
});

const HmlrDocumentDetailsCodec = t.intersection([
    t.type({ document_id: t.string, type: t.string, document_metadata: HmlrDocumentMetadataCodec }),
    t.partial({ companies_house_certification: t.boolean }),
]);

const HmlrApplicationLodgerCodec = t.intersection([
    t.type({ email: t.string, type: t.string }),
    t.partial({ telephone: t.string }),
]);

const SubstantiveApplicationDataCodec = t.intersection([
    t.type({
        customer_reference: t.string,
        application_lodger: HmlrApplicationLodgerCodec,
        type: t.string,
        titles: t.record(t.string, HmlrTitleDetailsCodec),
        transactions: t.record(t.string, HmlrTransactionCodec),
    }),
    t.partial({
        parties: t.record(t.string, HmlrPartyCodec),
        applicants: t.array(t.string),
        documents: t.record(t.string, HmlrDocumentDetailsCodec),
    }),
]);

const SubstantiveApplicationCodec = t.type({ data: SubstantiveApplicationDataCodec });
type TSubstantiveApplication = t.TypeOf<typeof SubstantiveApplicationCodec>;

const ApplicationRequestIdCodec = t.type({ application_request_id: t.string });
const RequisitionReplyCodec = t.type({});

const REQUISITION_MAX_CHARS = 4034;

type TLoadStatus = "idle" | "loading" | "ready" | "error";
type TSubmitStatus = "idle" | "submitting" | "success" | "error";
type TReplyStatus = "idle" | "submitting" | "success" | "error";
type TRegDataSaveStatus = "idle" | "saving" | "success" | "error";

interface IState {
    previewStatus: TLoadStatus;
    preview: TSubstantiveApplication | null;
    submitStatus: TSubmitStatus;
    applicationRequestId: string | null;
    submitError: string | null;
    confirmingSubmit: boolean;
    replyText: string;
    replyStatus: TReplyStatus;
    replyError: string | null;
    regDataMortgageMdReference: string;
    regDataDischargeSubmissionType: TDischargeSubmissionType | null;
    regDataDisclosableInterestsByTitle: Record<string, boolean>;
    regDataSaveStatus: TRegDataSaveStatus;
    regDataSaveError: string | null;
}

class CRMLegalCaseHmlrRegistrationContainer extends React.Component<TBlockContainerStateProps, IState> {

    public constructor(props: TBlockContainerStateProps) {
        super(props);
        this.state = {
            previewStatus: "idle",
            preview: null,
            submitStatus: "idle",
            applicationRequestId: null,
            submitError: null,
            confirmingSubmit: false,
            replyText: "",
            replyStatus: "idle",
            replyError: null,
            regDataMortgageMdReference: "",
            regDataDischargeSubmissionType: null,
            regDataDisclosableInterestsByTitle: {},
            regDataSaveStatus: "idle",
            regDataSaveError: null,
        };
    }

    public componentDidMount(): void {
        this.loadPreview();
    }

    private getCaseId(): string {
        return this.props.state.forms.case_details_page.data.output.details.children.id;
    }

    private loadPreview(): void {
        this.setState({ previewStatus: "loading", preview: null });
        getJson({
            url: `/v1/cases/${this.getCaseId()}/hmlr-application-preview`,
            dataCodec: t.union([SubstantiveApplicationCodec, t.null]),
            defaultData: null,
        })().then((response) => {
            if (response.tag === FirstPartyFetchResponse.constants.STATUS_2XX) {
                const preview = response.response.data;
                const prefill = preview !== null ? this.extractRegDataPrefill(preview) : {};
                this.setState({ previewStatus: "ready", preview, ...prefill });
            } else {
                this.setState({ previewStatus: "error" });
            }
        });
    }

    private extractRegDataPrefill(preview: TSubstantiveApplication): {
        regDataMortgageMdReference: string;
        regDataDischargeSubmissionType: TDischargeSubmissionType | null;
        regDataDisclosableInterestsByTitle: Record<string, boolean>;
    } {
        let regDataMortgageMdReference = "";
        let regDataDischargeSubmissionType: TDischargeSubmissionType | null = null;

        Object.values(preview.data.transactions).forEach((tx) => {
            if (tx.details !== undefined) {
                if (tx.details.type === "Charge" && tx.details.mdref !== undefined) {
                    regDataMortgageMdReference = tx.details.mdref;
                }
                if (tx.details.type === "Discharge" && tx.details.discharge_submission_type !== undefined) {
                    regDataDischargeSubmissionType = tx.details.discharge_submission_type;
                }
            }
        });

        const regDataDisclosableInterestsByTitle: Record<string, boolean> = {};
        Object.values(preview.data.titles).forEach((title) => {
            if (title.disclosable_overriding_interests !== undefined) {
                regDataDisclosableInterestsByTitle[title.title_number.toUpperCase()] = title.disclosable_overriding_interests;
            }
        });

        return { regDataMortgageMdReference, regDataDischargeSubmissionType, regDataDisclosableInterestsByTitle };
    }

    private handleRegDataMdRefChange(value: string): void {
        this.setState({ regDataMortgageMdReference: value });
    }

    private handleRegDataDischargeTypeChange(value: TDischargeSubmissionType): void {
        this.setState({ regDataDischargeSubmissionType: value });
    }

    private handleRegDataDisclosableInterestChange(titleNumber: string, value: boolean): void {
        this.setState((prev) => ({
            regDataDisclosableInterestsByTitle: {
                ...prev.regDataDisclosableInterestsByTitle,
                [titleNumber]: value,
            },
        }));
    }

    private handleRegDataSave(): void {
        const { regDataMortgageMdReference, regDataDischargeSubmissionType, regDataDisclosableInterestsByTitle } = this.state;
        this.setState({ regDataSaveStatus: "saving", regDataSaveError: null });

        const body: Record<string, unknown> = {};

        if (regDataMortgageMdReference.trim() !== "") {
            body["mortgage_md_reference"] = regDataMortgageMdReference.trim();
        } else {
            body["mortgage_md_reference"] = null;
        }

        if (regDataDischargeSubmissionType !== null) {
            body["discharge_submission_type"] = regDataDischargeSubmissionType;
        } else {
            body["discharge_submission_type"] = null;
        }

        body["disclosable_overriding_interests_by_title"] = regDataDisclosableInterestsByTitle;

        postJson({
            url: `/v1/cases/${this.getCaseId()}/hmlr-registration-data`,
            body,
            dataCodec: t.type({}),
            defaultData: {},
        })().then((response) => {
            if (response.tag === FirstPartyFetchResponse.constants.STATUS_2XX) {
                this.setState({ regDataSaveStatus: "success" });
                this.loadPreview();
            } else {
                this.setState({ regDataSaveStatus: "error", regDataSaveError: "Could not save registration data. Please try again." });
            }
        });
    }

    private handleSubmitRequest(): void {
        this.setState({ confirmingSubmit: true });
    }

    private handleSubmitCancel(): void {
        this.setState({ confirmingSubmit: false });
    }

    private handleSubmitConfirm(): void {
        this.setState({ submitStatus: "submitting", confirmingSubmit: false, submitError: null, applicationRequestId: null });
        postJson({
            url: `/v1/cases/${this.getCaseId()}/hmlr-application-submit`,
            body: {},
            dataCodec: ApplicationRequestIdCodec,
            defaultData: { application_request_id: "" },
        })().then((response) => {
            if (response.tag === FirstPartyFetchResponse.constants.STATUS_2XX) {
                this.setState({
                    submitStatus: "success",
                    applicationRequestId: response.response.data.application_request_id,
                });
            } else {
                this.setState({ submitStatus: "error", submitError: "Submission failed. Please try again or contact support." });
            }
        });
    }

    private handleReplyChange(value: string): void {
        this.setState({ replyText: value });
    }

    private handleReplySubmit(): void {
        const text = this.state.replyText.trim();
        if (!text) return;
        this.setState({ replyStatus: "submitting", replyError: null });
        postJson({
            url: `/v1/cases/${this.getCaseId()}/hmlr-requisition-reply`,
            body: { message_text: text },
            dataCodec: RequisitionReplyCodec,
            defaultData: {},
        })().then((response) => {
            if (response.tag === FirstPartyFetchResponse.constants.STATUS_2XX) {
                this.setState({ replyStatus: "success", replyText: "" });
            } else {
                this.setState({ replyStatus: "error", replyError: "Reply could not be sent. Please try again." });
            }
        });
    }

    public render(): JSX.Element {
        return (
            <CRMBlockScrollCacheView isShowing={true}>
                <BackgroundColour colour={CRMColors.NEUTRAL_PAPER}>
                    <MinHeight height="100vh">
                        <CRMSingleViewHeader title="HMLR Registration" />
                        <Padding spacing={CRMSpacing.MEDIUM}>
                            <SpacingColumn spacing={CRMSpacing.LARGE}>
                                {this.renderPreview()}
                                {this.renderRegistrationDataSection()}
                                {this.renderSubmitSection()}
                                {this.renderRequisitionReplySection()}
                            </SpacingColumn>
                        </Padding>
                    </MinHeight>
                </BackgroundColour>
            </CRMBlockScrollCacheView>
        );
    }

    private renderPreview(): JSX.Element {
        const { previewStatus, preview } = this.state;

        return (
            <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                <CRMTitleSection>Application preview</CRMTitleSection>

                {previewStatus === "loading" && (
                    <CRMParagraph>Loading application preview…</CRMParagraph>
                )}

                {previewStatus === "error" && (
                    <CRMParagraph>
                        Could not load the application preview. Check that all required case data is complete and try refreshing the page.
                    </CRMParagraph>
                )}

                {previewStatus === "ready" && preview !== null && (
                    <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                        <SpacingColumn spacing={CRMSpacing.SMALL}>
                            <CRMParagraph>
                                <WeightBold>Application type: </WeightBold>{preview.data.type}
                            </CRMParagraph>
                            <CRMParagraph>
                                <WeightBold>Customer reference: </WeightBold>{preview.data.customer_reference}
                            </CRMParagraph>
                            <CRMParagraph>
                                <WeightBold>Lodger email: </WeightBold>{preview.data.application_lodger.email}
                            </CRMParagraph>
                            <CRMParagraph>
                                <WeightBold>Lodger type: </WeightBold>{preview.data.application_lodger.type}
                            </CRMParagraph>
                        </SpacingColumn>

                        {preview.data.titles !== undefined && Object.keys(preview.data.titles).length > 0 && (
                            <SpacingColumn spacing={CRMSpacing.SMALL}>
                                <CRMParagraph><WeightBold>Titles</WeightBold></CRMParagraph>
                                {Object.entries(preview.data.titles).map(([ref, title]) => (
                                    <div key={ref} className="crm-hmlr-registration__row">
                                        <CRMParagraph>
                                            {title.title_number} — {title.scope_details.scope}
                                            {title.scope_details.part_description ? ` (${title.scope_details.part_description})` : ""}
                                        </CRMParagraph>
                                    </div>
                                ))}
                            </SpacingColumn>
                        )}

                        {preview.data.transactions !== undefined && Object.keys(preview.data.transactions).length > 0 && (
                            <SpacingColumn spacing={CRMSpacing.SMALL}>
                                <CRMParagraph><WeightBold>Dealings</WeightBold></CRMParagraph>
                                {Object.entries(preview.data.transactions).map(([ref, tx]) => (
                                    <div key={ref} className="crm-hmlr-registration__row">
                                        <CRMParagraph>
                                            {tx.type} — fee: £{(tx.fee_in_pence / 100).toFixed(2)}
                                            {tx.details && "value" in tx.details && tx.details.value !== undefined
                                                ? ` — value: £${(tx.details.value / 100).toFixed(2)}`
                                                : ""}
                                        </CRMParagraph>
                                    </div>
                                ))}
                            </SpacingColumn>
                        )}

                        {preview.data.parties !== undefined && Object.keys(preview.data.parties).length > 0 && (
                            <SpacingColumn spacing={CRMSpacing.SMALL}>
                                <CRMParagraph><WeightBold>Parties</WeightBold></CRMParagraph>
                                {Object.entries(preview.data.parties).map(([ref, party]) => (
                                    <div key={ref} className="crm-hmlr-registration__row">
                                        <CRMParagraph>
                                            {this.formatPartyName(party.details)} — {party.representation_type}
                                        </CRMParagraph>
                                    </div>
                                ))}
                            </SpacingColumn>
                        )}

                        {preview.data.documents !== undefined && Object.keys(preview.data.documents).length > 0 && (
                            <SpacingColumn spacing={CRMSpacing.SMALL}>
                                <CRMParagraph><WeightBold>Documents</WeightBold></CRMParagraph>
                                {Object.entries(preview.data.documents).map(([ref, doc]) => (
                                    <div key={ref} className="crm-hmlr-registration__row">
                                        <CRMParagraph>
                                            {doc.document_metadata.document_filename} ({doc.type})
                                        </CRMParagraph>
                                    </div>
                                ))}
                            </SpacingColumn>
                        )}
                    </SpacingColumn>
                )}
            </SpacingColumn>
        );
    }

    private formatPartyName(details: t.TypeOf<typeof HmlrPartyDetailsCodec>): string {
        if (details.type === "PRIVATE_INDIVIDUAL") {
            return [details.forenames, details.surname].filter(Boolean).join(" ");
        }
        if (details.type === "UK_COMPANY" || details.type === "OVERSEAS_COMPANY" || details.type === "OTHER_ORGANISATION") {
            return details.organisation_name;
        }
        if (details.type === "UNKNOWN") {
            return details.freeformat_name;
        }
        return "";
    }

    private renderRegistrationDataSection(): JSX.Element {
        const {
            preview,
            previewStatus,
            regDataMortgageMdReference,
            regDataDischargeSubmissionType,
            regDataDisclosableInterestsByTitle,
            regDataSaveStatus,
            regDataSaveError,
        } = this.state;

        const isSaving = regDataSaveStatus === "saving";

        const dischargeOptions: Array<{ value: TDischargeSubmissionType; text: string }> = [
            { value: "ATTACHED", text: "ATTACHED" },
            { value: "LENDER", text: "LENDER" },
            { value: "LATER", text: "LATER" },
        ];

        return (
            <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                <CRMTitleSection>Registration data</CRMTitleSection>

                {previewStatus !== "ready" && (
                    <CRMParagraph>Registration data fields will appear once the application preview has loaded.</CRMParagraph>
                )}

                {previewStatus === "ready" && (
                    <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                        <SpacingColumn spacing={CRMSpacing.SMALL}>
                            <CRMInputLabel>Charge MD reference</CRMInputLabel>
                            <CRMInputGeneralComponent
                                inputType="text"
                                displayError={false}
                                value={regDataMortgageMdReference}
                                placeholder="e.g. MD-1234"
                                disabled={isSaving}
                                onChange={(value) => this.handleRegDataMdRefChange(value)}
                            />
                        </SpacingColumn>

                        <SpacingColumn spacing={CRMSpacing.SMALL}>
                            <CRMInputLabel>Discharge submission type</CRMInputLabel>
                            <CRMRadioListComponent
                                options={dischargeOptions}
                                value={regDataDischargeSubmissionType}
                                disabled={isSaving}
                                onChange={(value) => this.handleRegDataDischargeTypeChange(value)}
                            />
                        </SpacingColumn>

                        {preview !== null && Object.values(preview.data.titles).length > 0 && (
                            <SpacingColumn spacing={CRMSpacing.SMALL}>
                                <CRMInputLabel>Disclosable overriding interests</CRMInputLabel>
                                {Object.values(preview.data.titles).map((title) => {
                                    const key = title.title_number.toUpperCase();
                                    const currentValue = regDataDisclosableInterestsByTitle[key] ?? false;
                                    return (
                                        <div key={key} className="crm-hmlr-registration__row">
                                            <SpacingRow spacing={CRMSpacing.SMALL} alignItems="center">
                                                <CRMParagraph>{title.title_number}</CRMParagraph>
                                                <CRMButtonPrimary
                                                    label="Yes"
                                                    highlight={currentValue ? "positive-green" : undefined}
                                                    disabled={isSaving}
                                                    onClick={() => this.handleRegDataDisclosableInterestChange(key, true)}
                                                />
                                                <CRMButtonPrimary
                                                    label="No"
                                                    highlight={!currentValue ? "instructional-brick" : undefined}
                                                    disabled={isSaving}
                                                    onClick={() => this.handleRegDataDisclosableInterestChange(key, false)}
                                                />
                                            </SpacingRow>
                                        </div>
                                    );
                                })}
                            </SpacingColumn>
                        )}

                        <SpacingRow spacing={CRMSpacing.SMALL} alignItems="center">
                            <CRMButtonPrimary
                                label={isSaving ? "Saving…" : "Save"}
                                disabled={isSaving}
                                onClick={() => this.handleRegDataSave()}
                            />
                        </SpacingRow>

                        {regDataSaveStatus === "success" && (
                            <CRMParagraph>Registration data saved.</CRMParagraph>
                        )}

                        {regDataSaveStatus === "error" && regDataSaveError !== null && (
                            <CRMParagraph>{regDataSaveError}</CRMParagraph>
                        )}
                    </SpacingColumn>
                )}
            </SpacingColumn>
        );
    }

    private renderSubmitSection(): JSX.Element {
        const { submitStatus, confirmingSubmit, applicationRequestId, submitError } = this.state;

        return (
            <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                <CRMTitleSection>Submit to Land Registry</CRMTitleSection>

                {submitStatus === "idle" && !confirmingSubmit && (
                    <SpacingColumn spacing={CRMSpacing.SMALL}>
                        <CRMParagraph>
                            Review the application preview above before submitting. Submission lodges the application directly with HM Land Registry and cannot be undone.
                        </CRMParagraph>
                        <SpacingRow spacing={CRMSpacing.SMALL}>
                            <CRMButtonPrimary
                                label="Submit to Land Registry"
                                onClick={() => this.handleSubmitRequest()}
                            />
                        </SpacingRow>
                    </SpacingColumn>
                )}

                {confirmingSubmit && (
                    <SpacingColumn spacing={CRMSpacing.SMALL}>
                        <CRMParagraph>
                            <WeightBold>Are you sure?</WeightBold> This will lodge the application with HM Land Registry. This action cannot be reversed.
                        </CRMParagraph>
                        <SpacingRow spacing={CRMSpacing.SMALL}>
                            <CRMButtonPrimary
                                label="Confirm and submit"
                                highlight="instructional-brick"
                                onClick={() => this.handleSubmitConfirm()}
                            />
                            <CRMButtonPrimary
                                label="Cancel"
                                onClick={() => this.handleSubmitCancel()}
                            />
                        </SpacingRow>
                    </SpacingColumn>
                )}

                {submitStatus === "submitting" && (
                    <CRMParagraph>Submitting application to Land Registry…</CRMParagraph>
                )}

                {submitStatus === "success" && applicationRequestId !== null && (
                    <SpacingColumn spacing={CRMSpacing.SMALL}>
                        <CRMParagraph>
                            Application submitted successfully.
                        </CRMParagraph>
                        <CRMParagraph>
                            <WeightBold>Application request ID: </WeightBold>{applicationRequestId}
                        </CRMParagraph>
                    </SpacingColumn>
                )}

                {submitStatus === "error" && submitError !== null && (
                    <CRMParagraph>{submitError}</CRMParagraph>
                )}
            </SpacingColumn>
        );
    }

    private renderRequisitionReplySection(): JSX.Element {
        const { replyText, replyStatus, replyError } = this.state;
        const isSubmitting = replyStatus === "submitting";
        const charsRemaining = REQUISITION_MAX_CHARS - replyText.length;

        return (
            <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                <CRMTitleSection>Reply to requisition</CRMTitleSection>
                <CRMParagraph>
                    Use this form to send a reply to an outstanding HMLR requisition. Keep your response concise and address the Land Registry's query directly.
                </CRMParagraph>
                <div className="crm-hmlr-registration__textarea-wrap">
                    <CRMTextAreaComponent
                        value={replyText}
                        placeholder="Type your reply here…"
                        disabled={isSubmitting}
                        onChange={(value) => this.handleReplyChange(value)}
                    />
                </div>
                <SpacingRow spacing={CRMSpacing.SMALL} alignItems="center">
                    <CRMButtonPrimary
                        label={isSubmitting ? "Sending…" : "Send reply"}
                        disabled={isSubmitting || replyText.trim().length === 0 || replyText.length > REQUISITION_MAX_CHARS}
                        onClick={() => this.handleReplySubmit()}
                    />
                    <CRMParagraph>
                        {charsRemaining < 0
                            ? `${Math.abs(charsRemaining)} characters over limit`
                            : `${charsRemaining} characters remaining`}
                    </CRMParagraph>
                </SpacingRow>

                {replyStatus === "success" && (
                    <CRMParagraph>Reply sent successfully.</CRMParagraph>
                )}

                {replyStatus === "error" && replyError !== null && (
                    <CRMParagraph>{replyError}</CRMParagraph>
                )}
            </SpacingColumn>
        );
    }
}

export default CRMLegalCaseHmlrRegistrationContainer;
